import { Children, useCallback, useId, useState } from "react";

import { useStableRest } from "../../../../utils/use-stable-rest";
import {
  KANBAN_EMPTY_MESSAGE,
  KANBAN_LOAD_MORE_LABEL,
  KANBAN_PAGE_SIZE,
} from "../../kanban.constants";
import { useKanbanContext } from "../../kanban.context";
import type { KanbanColumnProps, KanbanColumnViewModel, KanbanShowMoreState } from "./types-column";

function showMoreState(shown: number, available: number, pageSize: number): KanbanShowMoreState {
  if (shown >= available) return "todas";
  return shown > pageSize ? "mais" : "recolhido";
}

/** ViewModel do `Kanban.Column`: lote visível, estado do "Mostrar mais", vazio e testids; sem JSX. */
export function useKanbanColumn({
  title,
  count,
  total,
  pageSize = KANBAN_PAGE_SIZE,
  onLoadMore,
  loadingMore = false,
  loading = false,
  loadingLabel,
  error,
  emptyMessage = KANBAN_EMPTY_MESSAGE,
  loadMoreLabel = KANBAN_LOAD_MORE_LABEL,
  testId,
  className,
  style,
  children,
  ...props
}: KanbanColumnProps): KanbanColumnViewModel {
  useKanbanContext();
  const rest = useStableRest(props);
  const titleId = useId();
  const [visible, setVisible] = useState(pageSize);

  const items = Children.toArray(children);
  const serverPaged = total !== undefined;
  const shown = serverPaged ? items : items.slice(0, visible);
  const available = serverPaged ? total : items.length;
  const blocked = loading || Boolean(error);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    if (!serverPaged) setVisible((current) => current + pageSize);
    onLoadMore?.();
  }, [loadingMore, serverPaged, pageSize, onLoadMore]);

  const part = (name: string) => (testId ? `${testId}-${name}` : undefined);

  return {
    rest,
    className,
    style,
    title,
    titleId,
    count: count ?? available,
    loading,
    loadingLabel,
    error: blocked && !loading ? error : null,
    cards: blocked ? [] : shown,
    isEmpty: !blocked && available === 0,
    emptyMessage,
    showMore: showMoreState(shown.length, available, pageSize),
    showLoadMore: !blocked && shown.length < available,
    loadingMore,
    loadMoreLabel,
    loadMore,
    testId,
    countTestId: part("count"),
    emptyTestId: part("empty"),
    loadingTestId: part("loading"),
    loadMoreTestId: part("load-more"),
    bodyTestId: part("body"),
  };
}
